/**
 * Contact-prep lead cards and outreach/acquisition forms.
 */
import { getDashboardOperator } from "./dashboard-auth.js";
import { escapeAttr, escapeHtml, labelize } from "./dashboard-html.js";
import { contactPolicyReviewHtml } from "./dashboard-contact-policy.js";
import { leadGenerationOverflowHtml, leadRowPrimaryActionsHtml, mappedLeadGenerationNextAction } from "./dashboard-operator-view.js";
import type { LeadFactoryLead } from "./dashboard-lead-factory-types.js";

const leadFactoryOutreachChannels = ["phone", "letter", "email", "skip_trace", "pi", "note"] as const;
const leadFactoryOutreachOutcomes = [
  "attempted",
  "connected",
  "interested",
  "left_message",
  "sent",
  "returned_mail",
  "bad_contact",
  "needs_follow_up",
  "not_interested",
  "do_not_contact",
  "pi_needed",
  "skip_trace_needed"
] as const;
const leadFactoryAcquisitionStages = ["researching", "interested", "offer_pending", "negotiating", "under_contract", "closed_won", "closed_lost", "dead"] as const;
const leadFactoryOfferStrategies = ["cash_as_is", "subject_to_review", "low_equity_review"] as const;

export function contactLeadHtml(lead: LeadFactoryLead): string {
  const next = mappedLeadGenerationNextAction(lead);
  return `
    <article class="contact-item" data-lead-id="${escapeAttr(lead.id)}">
      <div>
        <strong>${escapeHtml(lead.property_address || lead.owner_name || lead.id)}</strong>
        <small>${escapeHtml(lead.county)} · ${escapeHtml(labelize(lead.contact_readiness))}</small>
      </div>
      <div class="row-actions">
        ${leadRowPrimaryActionsHtml(lead.id, next)}
        ${leadGenerationOverflowHtml(lead.id, next?.action)}
      </div>
    </article>
  `;
}

export function contactOutreachFormHtml(lead: LeadFactoryLead): string {
  const defaults = defaultOutreachForReadiness(lead.contact_readiness);
  return `
    <form class="structured-action-form contact-outreach-form" data-contact-outreach-form data-lead-id="${escapeAttr(lead.id)}" data-lead-status="${escapeAttr(lead.status)}" data-contact-readiness="${escapeAttr(lead.contact_readiness)}">
      <select aria-label="Outreach channel" data-contact-outreach-channel>
        ${leadFactoryOutreachChannels.map((channel) => optionHtml(channel, defaults.channel)).join("")}
      </select>
      <select aria-label="Outreach outcome" data-contact-outreach-outcome>
        ${leadFactoryOutreachOutcomes.map((outcome) => optionHtml(outcome, defaults.outcome)).join("")}
      </select>
      <input type="datetime-local" aria-label="Follow-up at" data-contact-outreach-follow-up>
      ${contactPolicyReviewHtml(lead.status, lead.contact_readiness, defaults.channel, defaults.outcome, defaults.policyOverrideReason, "")}
      <input type="text" maxlength="160" value="Dashboard recorded structured outreach." aria-label="Outreach note" data-contact-outreach-note>
      <button class="button secondary" type="submit">Record outreach</button>
    </form>
  `;
}

export function acquisitionPipelineFormHtml(lead: LeadFactoryLead): string {
  const valueEstimate = lead.mortgage_amount && lead.mortgage_amount > 0 ? String(lead.mortgage_amount) : "";
  return `
    <form class="structured-action-form acquisition-pipeline-form" data-acquisition-pipeline-form data-lead-id="${escapeAttr(lead.id)}">
      <select aria-label="Acquisition stage" data-acquisition-pipeline-stage>
        ${leadFactoryAcquisitionStages.map((stage) => optionHtml(stage, "interested")).join("")}
      </select>
      <input type="number" min="0" step="1000" value="${escapeAttr(valueEstimate)}" aria-label="Value estimate" data-acquisition-pipeline-value>
      <input type="text" maxlength="120" value="${escapeAttr(getDashboardOperator() || "unassigned")}" aria-label="Pipeline assignee" data-acquisition-pipeline-assigned>
      <input type="text" maxlength="160" value="Dashboard moved lead into acquisition pipeline." aria-label="Pipeline note" data-acquisition-pipeline-note>
      <button class="button secondary" type="submit">Move pipeline</button>
    </form>
  `;
}

export function acquisitionOfferFormHtml(lead: LeadFactoryLead): string {
  const valueEstimate = lead.mortgage_amount && lead.mortgage_amount > 0 ? lead.mortgage_amount : null;
  const defaultOffer = valueEstimate ? Math.max(1000, Math.round(valueEstimate * 0.64)) : null;
  return `
    <form class="structured-action-form acquisition-offer-form" data-acquisition-offer-form data-lead-id="${escapeAttr(lead.id)}">
      <select aria-label="Offer strategy" data-acquisition-offer-strategy>
        ${leadFactoryOfferStrategies.map((strategy) => optionHtml(strategy, "cash_as_is")).join("")}
      </select>
      <input type="number" min="0" step="1000" value="${escapeAttr(valueEstimate === null ? "" : String(valueEstimate))}" aria-label="Offer value estimate" data-acquisition-offer-value>
      <input type="number" min="0" step="1000" value="${escapeAttr(defaultOffer === null ? "" : String(defaultOffer))}" aria-label="Offer amount" data-acquisition-offer-amount>
      <input type="number" min="0" step="100" aria-label="Earnest money" data-acquisition-offer-earnest>
      <input type="number" min="1" step="1" aria-label="Closing days" data-acquisition-offer-closing-days>
      <input type="text" maxlength="160" value="Dashboard generated structured offer package." aria-label="Offer note" data-acquisition-offer-note>
      <button class="button secondary" type="submit">Generate offer</button>
    </form>
  `;
}

export function optionHtml(value: string, selected: string): string {
  return `<option value="${escapeAttr(value)}" ${value === selected ? "selected" : ""}>${escapeHtml(labelize(value))}</option>`;
}

export function defaultOutreachForReadiness(readiness: string): { channel: string; outcome: string; policyOverrideReason: string } {
  if (readiness === "needs_skip_trace") return { channel: "skip_trace", outcome: "skip_trace_needed", policyOverrideReason: "" };
  if (readiness === "needs_pi") return { channel: "pi", outcome: "pi_needed", policyOverrideReason: "" };
  if (readiness === "ready_for_call") return { channel: "phone", outcome: "attempted", policyOverrideReason: "operator_policy_review" };
  return { channel: "letter", outcome: "sent", policyOverrideReason: "" };
}

